import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/motion/Reveal";
import { BOOKS, ORGANIZATIONS } from "../data/resources";

const Book = ({ book, index }) => (
  <Reveal delay={index * 0.04} y={20}>
    <div
      data-testid={`resource-book-${book.id}`}
      className="group grid grid-cols-[3.5rem_1fr] gap-6 border-t hairline py-8 sm:grid-cols-[5rem_1fr_auto] sm:gap-10 lg:py-10"
    >
      <div
        className="relative h-24 w-14 shadow-[0_8px_20px_rgba(28,25,23,0.12)] transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-[-2deg] sm:h-28 sm:w-20"
        style={{ backgroundColor: book.tone }}
        aria-hidden="true"
      >
        <span className="absolute inset-y-0 left-1.5 w-px bg-white/25" />
        <span className="absolute inset-x-2 bottom-3 h-px bg-white/30" />
        <span className="absolute inset-x-2 bottom-5 h-px bg-white/30" />
      </div>
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-terracotta">{book.level}</span>
        <h3 className="mt-2 font-serif text-3xl leading-tight text-ink">{book.title}</h3>
        <p className="mt-1 text-sm text-ink-muted">
          {book.author} · {book.year}
        </p>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink-soft sm:text-base">{book.description}</p>
        {book.url && (
          <a
            href={book.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`resource-book-buy-${book.id}`}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-xs font-medium text-ink transition-colors duration-300 hover:border-terracotta hover:bg-terracotta hover:text-parchment"
          >
            Buy the book <ArrowUpRight size={12} />
          </a>
        )}
      </div>
      <span className="hidden font-serif text-5xl leading-none text-ink/10 sm:block">0{index + 1}</span>
    </div>
  </Reveal>
);

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <PageHeader
        testId="resources-header"
        eyebrow="Books & Resources"
        lines={["A short shelf,", "chosen with care."]}
        lede="You do not need a library to begin — a few trusted books and a knowledgeable teacher will carry you a long way. Here is where we would start."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <Reveal className="lg:sticky lg:top-32">
              <p className="eyebrow">Books</p>
              <h2 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
                Start gentle, <span className="italic text-ink-soft">go deep.</span>
              </h2>
              <p className="mt-6 max-w-sm text-sm leading-relaxed text-ink-soft">
                Books you can buy directly are listed first, followed by the classic Noahide
                guides and the primary source itself. The “level” label will help you choose a
                first read.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8 lg:col-start-5">
            {BOOKS.map((b, i) => (
              <Book key={b.id} book={b} index={i} />
            ))}
            <div className="border-t hairline" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <Reveal>
          <p className="eyebrow">Organisations & teachers</p>
          <h2 className="mt-5 max-w-2xl font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl">
            People who will <span className="italic text-ink-soft">answer your questions.</span>
          </h2>
        </Reveal>
        <Stagger className="mt-14 grid grid-cols-1 gap-px bg-ink/10 md:grid-cols-2 lg:grid-cols-3">
          {ORGANIZATIONS.map((o) => (
            <StaggerItem key={o.id}>
              <a
                href={o.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`resource-org-${o.id}`}
                className="group flex h-full flex-col justify-between bg-parchment p-8 transition-colors duration-500 hover:bg-white lg:p-10"
              >
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-serif text-2xl leading-tight text-ink sm:text-3xl">{o.name}</h3>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border hairline transition-all duration-500 group-hover:border-terracotta group-hover:bg-terracotta group-hover:text-parchment">
                    <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:rotate-45" />
                  </span>
                </div>
                <p className="mt-8 text-sm leading-relaxed text-ink-soft">{o.description}</p>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
                  {o.url.replace("https://", "")}
                </p>
              </a>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal className="mt-20 border-l-2 border-terracotta pl-6 sm:pl-8">
          <p className="max-w-2xl font-serif text-2xl leading-snug text-ink sm:text-3xl">
            A note of humility: this site is an introduction, not a ruling. For questions about
            your own situation, speak with an Orthodox rabbi experienced in guiding Noahides.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
